import * as React from "react";
import DataList from "../DataList";
import "../workspace/workspaceContainer.scss"
import {api} from "../../../../api/api";
import {WorkspaceView} from "../../../../model/workspace/WorkspaceView";
import {none, option, Option} from "ts-option";
import FiltersContainer from "../../filters/filtersContainer/FiltersContainer";
import PageSelectionComponent from "../paginator/PageSelectionComponent";
import Loading from "../../../ui/loading/Loading";
import {notifyBugsnagAndShowToast} from "../../../../utils/Utils";
import SortColumnModel from "../../../../model/workspace/SortColumnModel";
import WildcardFilter, {WildcardFilterColumnType} from "../../../../model/workspace/WildcardFilter";
import WildcardSearch from "../filter/WildcardSearch";
import {CellInfo} from "react-table";
import {WorkspaceName} from "../workspace/WorkspaceContainer";
import Filter from "../../../../model/workspace/Filter";
import LocalStorage from "../../../../store/local/LocalStorage";

interface ComponentProps {
	/**
	 * Workspace name.
	 */
	workspaceName: WorkspaceName;
	/**
	 * WildcardFilter column type
	 */
	wildcardFilterColumnType: WildcardFilterColumnType;
	/**
	 * Data list row click callback.
	 * @param {string} rowId
	 * @param {any} rowData
	 */
	onRowClick?: (rowId: string, rowData: any) => void;
	/**
	 * Get row id from row object.
	 * @param row
	 * @returns {string}
	 */
	rowToId: (row: any) => string;
	/**
	 * Indicates if workspace columns are sortable
	 */
	sortable?: boolean
	/**
	 * Default sort
	 */
	defaultSort?: Option<SortColumnModel>
	/**
	 * Last column content
	 * @returns {JSX.Element}
	 */
	lastColumn?: (row: CellInfo) => JSX.Element;
	/**
	 * Custom width of last column.
	 */
	lastColumnWidth?: number;
	/**
	 * Returns if passed row is ignored.
	 * @param rowData
	 * @returns {boolean}
	 */
	isRowIgnored?: (rowData: any) => boolean;
}

interface TableData {
	totalItems: number;
	currentPageNo: number;
	items: any[]
}

interface IState {
	view: Option<WorkspaceView>;
	data: TableData;
	isLoading: boolean;
	wildcardFilter: Option<WildcardFilter>;
	sortColumn: Option<SortColumnModel>
}

type Props = ComponentProps;

export default class WorkspaceLiteContainer extends React.Component<Props, IState> {

	public static PAGE_SIZE = 20;

	constructor(props: Props) {
		super(props);
		this.state =
			{
				view: none,
				data: {totalItems: 0, currentPageNo: 1, items: []},
				isLoading: false,
				wildcardFilter: none,
				sortColumn: props.defaultSort ? props.defaultSort : none
			};

		this.onPageChange = this.onPageChange.bind(this);
		this.refreshCurrentPage = this.refreshCurrentPage.bind(this);
		this.onFiltersChange = this.onFiltersChange.bind(this);
	}

	componentDidMount() {
		const workspaceSearch: string = LocalStorage.getWorkspaceSearch(this.props.workspaceName);
		if (workspaceSearch) {
			this.searchWithSavedQuery(workspaceSearch);
		}
		this.getView().then((viewData => {
					this.setState({
						view: option(viewData)
					})
				}
			)
		).catch((err) => notifyBugsnagAndShowToast(err, "Fetch view"));
	}

	render() {
		const tableData = this.state.data;
		const loadingText = !this.state.isLoading && this.state.data.items.length == 0 ?
			(this.state.view.map(view => view.filters.length).orNull ? `No ${this.props.workspaceName} found` :
				`Add filter to search ${this.props.workspaceName}`) : "";
		return (
			<Loading isLoading={this.state.view.isEmpty && this.state.wildcardFilter.isEmpty}>
				{this.state.view.map(view => (
						<div className={"c-workspace"}>
							<div className={"c-workspace_actions"}>
								<div className={"actions-wrapper"}>
									<WildcardSearch onFilterChange={(filter) => this.setState({wildcardFilter: filter},
										() => this.onPageChange(1))}
													isLoading={this.state.isLoading}
													filterValue={this.state.wildcardFilter}
													workspaceName={this.props.workspaceName}/>
									<FiltersContainer columns={view.columns}
													  filters={view.filters}
													  onFiltersChange={this.onFiltersChange}
													  isExpanded={true}
													  workspaceName={this.props.workspaceName}/>

								</div>
								<PageSelectionComponent itemsNumber={tableData.totalItems}
														itemsPerPage={WorkspaceLiteContainer.PAGE_SIZE}
														currentPageIndex={tableData.currentPageNo}
														onChange={this.onPageChange}/>
							</div>
							<DataList columns={view.columns}
									  onColumnsChange={(columns) => this.setState({view: option(view.copy({columns}))})}
									  hideColumnCheckbox={true}
									  data={this.state.data.items}
									  onRowClick={(rowId, rowData) => this.props.onRowClick ?
										  this.props.onRowClick(rowId, rowData) : null}
									  rowToId={this.props.rowToId}
									  isLoading={this.state.isLoading}
									  noDataText={loadingText}
									  onSortingChange={(sortColumn) => { this.setState({sortColumn}, this.refreshData) }}
									  sortColumn={this.state.sortColumn}
									  sortable={this.props.sortable}
									  lastColumn={this.props.lastColumn}
									  lastColumnWidth={this.props.lastColumnWidth}
									  refreshCurrentPage={this.refreshCurrentPage}
									  isRowIgnored={this.props.isRowIgnored}
									  workspaceName={this.props.workspaceName}/>
						</div>
					)
				).orNull
				}
			</Loading>
		)
	}

	searchWithSavedQuery = (searchQuery: string) => {
		const wildcardFilter = new WildcardFilter(searchQuery);
		this.setState({wildcardFilter: option(wildcardFilter)},
			() => this.onPageChange(1))
	};

	onFiltersChange(filters: Filter[], refreshData: boolean) {
		this.state.view.map(view => {
			let newState: any = {
				view: option(view.copy({filters}))
			};
			refreshData && (newState.wildcardFilter = none);
			this.setState(newState,
				() => refreshData && this.refreshData());
		});
	}

	onPageChange(pageNo: number, wildcardFilter: WildcardFilter = this.state.wildcardFilter.orNull) {
		if (this.state.view.map(view => view.filters.length).orNull || wildcardFilter) {
			this.setState({isLoading: true}, () =>
				this.getData(pageNo, wildcardFilter).then((data) => {
						this.setState(
							{
								data: {
									totalItems: data.totalItems,
									currentPageNo: pageNo,
									items: data.items
								},
								isLoading: false
							}, () => {
								if (wildcardFilter) {
									LocalStorage.saveWorkspaceSearch(this.props.workspaceName, wildcardFilter.value);
								} else {
									LocalStorage.saveWorkspaceSearch(this.props.workspaceName, "");
								}
							});
					}
				).catch(
					(err) => this.setState({isLoading: false}, () => notifyBugsnagAndShowToast(err, "Fetch workspace lite data")))
			)
		}
		else {
			this.setState({data: {totalItems: 0, currentPageNo: 1, items: []}});
			LocalStorage.saveWorkspaceSearch(this.props.workspaceName, "");
		}
	}

	getView(): Promise<WorkspaceView> {
		return api.instance.workspaceApi.fetchWorkspaceLiteView(this.props.workspaceName);
	}

	refreshData() {
		this.onPageChange(1);
	}

	refreshCurrentPage() {
		this.onPageChange(this.state.data.currentPageNo);
	}

	getData(pageNumber: number, wildcardFilter?: WildcardFilter) {
		let filters = [];
		if (wildcardFilter) {
			filters = [wildcardFilter.toQueryJson(this.props.wildcardFilterColumnType)];
		}
		else {
			filters = this.state.view.map(sv => sv.filters.filter(f => f.type !== null).map(f => f.toQueryJson()))
				.getOrElse([]);
		}
		const sort = this.state.sortColumn.map(sc => sc.toQueryString()).orNull;
		return api.instance.workspaceApi.fetchWorkspaceData(this.props.workspaceName, pageNumber, filters, sort);
	}
}